import React, { useState } from "react";
import { useFormik } from "formik";
import { Button, useMediaQuery, useTheme } from "@material-ui/core";
import { useIntl } from "react-intl";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { useSnackbar } from "notistack";
import {
  MeetingTemplateKindType,
  MeetingTemplateResponse,
} from "../../../generated/meetingtemplate-api";
import { messages } from "./ModifyMeetingTemplateDialog.messages";
import { useAuthenticatedUser } from "../../../Providers/AuthenticatedUserProvider/AuthenticatedUserProvider";
import { useCustomerId } from "../../../Providers/CustomerProvider/CustomerProvider";
import MeetingTemplateForm, {
  MeetingTemplateFormikValues,
} from "../../Forms/MeetingTemplateForm/MeetingTemplateForm";
import { meetingTemplateService } from "../../../Providers/ServiceProvider/ServiceProvider";
import MeetingTemplateFormValidationSchema from "../../Forms/MeetingTemplateForm/Components/Validation/MeetingTemplateFormValidationSchema";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  meetingTemplate: MeetingTemplateResponse;
  onUpdated?: () => void;
}

const ModifyMeetingDialog = (props: Props) => {
  const { isOpen, onClose, meetingTemplate, onUpdated } = props;
  const intl = useIntl();
  const theme = useTheme();
  const customerId = useCustomerId();
  const { enqueueSnackbar } = useSnackbar();
  const [authenticatedUser] = useAuthenticatedUser();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [templateType] = useState<MeetingTemplateKindType | undefined>(
    MeetingTemplateKindType.Personal
  );

  const initialValues = {
    title: meetingTemplate.title || "",
    duration: meetingTemplate.duration || 30,
    templateType: meetingTemplate.type || undefined,
  } as MeetingTemplateFormikValues;

  const [kindTypeError] = useState<string>("");
  const formik = useFormik({
    initialValues,
    validationSchema: MeetingTemplateFormValidationSchema,
    onSubmit: (values) => {
      if (customerId && authenticatedUser?.user) {
        meetingTemplateService()
          .updateMeetingTemplate({
            customerId,
            templateId: meetingTemplate.id,
            updateMeetingTemplateRequest: {
              title: values.title,
              duration: values.duration,
            },
          })
          .then(() =>
            enqueueSnackbar(intl.formatMessage(messages.success), {
              variant: "success",
            })
          )
          .then(onUpdated)
          .catch(() =>
            enqueueSnackbar(intl.formatMessage(messages.generalError), {
              variant: "error",
            })
          )
          .finally(onClose);
      }
    },
  });

  return (
    <Dialog
      onClick={(e) => e.stopPropagation()}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        {intl.formatMessage(messages.dialogTitle)}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <MeetingTemplateForm
            formik={formik}
            errorType={kindTypeError}
            typeDefaultValue={templateType}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" type="submit" color="primary">
            {intl.formatMessage(messages.submitButtonLabel)}
          </Button>
          <Button onClick={onClose} color="primary">
            {intl.formatMessage(messages.cancelButtonLabel)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModifyMeetingDialog;
